export type CacheEndpoint = 'valueStreams' | 'valueStreamDetails' | 'metrics' | 'outcomes';

const CAPTURE_FLAG_PREFIX = 'cacheCapture.';
const CACHED_DATA_PREFIX = 'cachedData.';
const USE_CACHED_DATA_KEY = 'useCachedData';

const ALL_ENDPOINTS: CacheEndpoint[] = ['valueStreams', 'valueStreamDetails', 'metrics', 'outcomes'];

export const CACHE_SETTINGS_CHANGED_EVENT = 'cache-settings-changed';

function dispatchCacheSettingsChanged(): void {
  try {
    window.dispatchEvent(new CustomEvent(CACHE_SETTINGS_CHANGED_EVENT));
  } catch {
    // ignore — not in a browser context
  }
}

export function enableCapture(): void {
  try {
    for (const ep of ALL_ENDPOINTS) {
      sessionStorage.setItem(`${CAPTURE_FLAG_PREFIX}${ep}`, 'true');
    }
  } catch {
    console.error('Failed to enable capture in sessionStorage');
  }
}

export function isCaptureEnabled(endpoint: CacheEndpoint): boolean {
  try {
    return sessionStorage.getItem(`${CAPTURE_FLAG_PREFIX}${endpoint}`) === 'true';
  } catch {
    return false;
  }
}

export function disableCapture(endpoint: CacheEndpoint): void {
  try {
    sessionStorage.removeItem(`${CAPTURE_FLAG_PREFIX}${endpoint}`);
  } catch {
    console.error(`Failed to disable capture for ${endpoint}`);
  }
}

export function saveResponse(endpoint: CacheEndpoint, data: unknown): void {
  try {
    localStorage.setItem(`${CACHED_DATA_PREFIX}${endpoint}`, JSON.stringify(data));
    dispatchCacheSettingsChanged();
  } catch {
    console.error(`Failed to save response for ${endpoint}`);
  }
}

export function getResponse<T = unknown>(endpoint: CacheEndpoint): T | null {
  try {
    const raw = localStorage.getItem(`${CACHED_DATA_PREFIX}${endpoint}`);
    if (raw === null) return null;
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

export function hasResponse(endpoint: CacheEndpoint): boolean {
  try {
    return localStorage.getItem(`${CACHED_DATA_PREFIX}${endpoint}`) !== null;
  } catch {
    return false;
  }
}

export function hasAnyResponse(): boolean {
  return ALL_ENDPOINTS.some(hasResponse);
}

export function isCachingEnabled(): boolean {
  try {
    return localStorage.getItem(USE_CACHED_DATA_KEY) === 'true';
  } catch {
    return false;
  }
}

export function setCachingEnabled(value: boolean): void {
  try {
    localStorage.setItem(USE_CACHED_DATA_KEY, value ? 'true' : 'false');
    dispatchCacheSettingsChanged();
  } catch {
    console.error('Failed to set caching enabled in localStorage');
  }
}

